import { Link, Skeleton, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { FlowFieldDataType } from 'flow-schema';
import { Link as ReactRouterLink } from 'react-router-dom';
import { type z } from 'zod';

import { Text, api, getQueryProps, isDefined, isTruthy } from 'common';

import { useFlowDefinitionContext } from '../../../../../context';
import { BlockAutocomplete } from '../../../ConfigFields/Autocomplete';
import { FilterConditionFields } from '../../../ConfigFields/FilterConditionFields';
import { useSelectedNodeField } from '../../../ConfigFields/useSelectedNodeField';

const SUPPORTED_DATA_TYPES = [
  FlowFieldDataType.String,
  FlowFieldDataType.Number,
  FlowFieldDataType.Boolean,
];

export function ReferenceTableFormFields() {
  const {
    flowDefinition: { organizationId },
  } = useFlowDefinitionContext();
  const { data: referenceTables } = useQuery({
    ...getQueryProps(api.client.FlowReferenceTable, 'findAll', {
      organizationId,
    }),
  });
  const { value: referenceTableId } =
    useSelectedNodeField<
      z.infer<typeof api.FlowDefinition.GetReferenceTableConfigSchema>['referenceTableId']
    >('config.referenceTableId');
  const selectedReferenceTable = referenceTables?.find((table) => table.id === referenceTableId);
  const { data: columnNames, isLoading } = useQuery({
    ...getQueryProps(
      api.client.FlowReferenceTable,
      'getColumnNames',
      referenceTableId || 'INVALID_ID'
    ),
    enabled: isTruthy(referenceTableId),
  });

  if (isLoading) {
    return <Skeleton />;
  }

  return (
    <Stack gap={2}>
      <BlockAutocomplete
        fieldName="config.referenceTableId"
        label="Reference Table"
        options={referenceTables?.map((referenceTable) => referenceTable.id) || []}
        getOptionLabel={(referenceTableId) =>
          referenceTables?.find((referenceTable) => referenceTable.id === referenceTableId)?.name ||
          'Search'
        }
        helperText="Select a reference table to use for this block"
      />
      {isDefined(selectedReferenceTable) && (
        <>
          <Link
            component={ReactRouterLink}
            to={`/agents/flows/referenceTables/${referenceTableId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {`View ${selectedReferenceTable?.name} in Reference Tables`}
          </Link>
        </>
      )}
      <Text>
        Don&apos;t see the reference table you&apos;re looking for? Create one{' '}
        <Link
          component={ReactRouterLink}
          to="/agents/flows/referenceTables"
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </Link>
        .
      </Text>
      {isDefined(selectedReferenceTable) && (
        <>
          <BlockAutocomplete
            fieldName="config.selectedColumns"
            label="Columns"
            multiple={true}
            options={columnNames || []}
            helperText="Select one or more columns from the reference table"
          />
          <FilterConditionFields
            supportedDataTypes={SUPPORTED_DATA_TYPES}
            fieldsToFilter={columnNames || []}
          />
        </>
      )}
    </Stack>
  );
}
